/**
 * Sandbox Command Center — REDEX Platform Operations
 *
 * Full control panel for every sandbox tenant. Operators can:
 *   • Monitor trial health, usage, and conversion funnel
 *   • Navigate directly to any tenant's branded portal
 *   • Extend trials, resend invites, impersonate users
 *   • Copy tenant portal URLs for sharing
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  ChevronDown, ChevronRight, Users, FlaskConical, RefreshCw, Mail,
  LogIn, ExternalLink, Copy, Clock, TrendingUp, AlertTriangle,
  CheckCircle, Circle, Plus, Eye, Zap, Trash2, UserPlus
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../../services/api';
import { Card, CardContent, CardHeader } from '../../components/new-components/ui/card';
import { Button } from '../../components/new-components/ui/button';
import { Badge } from '../../components/new-components/ui/badge';
import { toast } from 'sonner';
import { useUser } from '../../contexts/UserContext';

// ─── Types ────────────────────────────────────────────────────────────────────
interface SandboxTenantRow {
  tenant_id: string;
  company_name: string;
  wizard_completed: boolean;
  launched_at: string | null;
  created_at: string;
  primary_color: string;
  accent_color: string;
  admin_count: number;
  investor_count: number;
  trial_expires_at: string | null;
  esigns_used: number;
  esigns_limit: number;
  offerings_count: number;
  offerings_limit: number;
  properties_count: number;
  properties_limit: number;
  investors: Array<{
    id: string;
    email: string;
    name: string | null;
    accepted_at: string | null;
    invite_sent_at: string | null;
    user_id: string | null;
  }>;
  admins: Array<{
    id: string;
    email: string;
    accepted_at: string | null;
    user_id: string | null;
  }>;
}

type FilterTab = 'all' | 'active' | 'expiring' | 'expired' | 'incomplete';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString() : '—');

function daysLeft(expiresAt: string | null): number | null {
  if (!expiresAt) return null;
  return Math.ceil((new Date(expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

function trialStatus(t: SandboxTenantRow): 'active' | 'expiring' | 'expired' | 'none' {
  const d = daysLeft(t.trial_expires_at);
  if (d === null) return 'none';
  if (d > 3) return 'active';
  if (d > 0) return 'expiring';
  return 'expired';
}

function conversionRate(t: SandboxTenantRow): number {
  if (!t.investors?.length) return 0;
  const accepted = t.investors.filter(i => i.accepted_at).length;
  return Math.round((accepted / t.investors.length) * 100);
}

function totalActivity(t: SandboxTenantRow): number {
  return t.esigns_used + t.offerings_count + t.properties_count;
}

const PORTAL_BASE = window.location.origin;

const portalUrl = (slug: string) => `${PORTAL_BASE}/${slug}/portal`;
const loginUrl  = (slug: string) => `${PORTAL_BASE}/${slug}/login`;

// ─── Sub-components ────────────────────────────────────────────────────────────
const UsageBar: React.FC<{ used: number; limit: number; label: string }> = ({ used, limit, label }) => {
  const pct = limit > 0 ? Math.min(used / limit, 1) : 0;
  const color = pct >= 1 ? 'bg-red-500' : pct >= 0.75 ? 'bg-amber-500' : 'bg-green-500';
  return (
    <div className="min-w-[80px]">
      <div className="flex justify-between mb-0.5">
        <span className="text-[10px] text-muted-foreground">{label}</span>
        <span className="text-[10px] font-mono text-muted-foreground">{used}/{limit}</span>
      </div>
      <div className="h-1 bg-muted rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct * 100}%` }} />
      </div>
    </div>
  );
};

const TrialBadge: React.FC<{ tenant: SandboxTenantRow }> = ({ tenant }) => {
  const status = trialStatus(tenant);
  const d = daysLeft(tenant.trial_expires_at);
  if (status === 'active') {
    return <Badge className="text-[10px] bg-green-50 text-green-700 border-green-200">{d}d left</Badge>;
  }
  if (status === 'expiring') {
    return <Badge className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">{d}d left</Badge>;
  }
  if (status === 'expired') {
    return <Badge className="text-[10px] bg-red-50 text-red-700 border-red-200">Expired</Badge>;
  }
  return <Badge variant="secondary" className="text-[10px]">—</Badge>;
};

const FunnelBar: React.FC<{ tenant: SandboxTenantRow }> = ({ tenant }) => {
  const invited  = tenant.investors?.length || 0;
  const accepted = tenant.investors?.filter(i => i.accepted_at).length || 0;
  const rate     = invited > 0 ? Math.round((accepted / invited) * 100) : 0;
  return (
    <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
      <span>{invited} invited</span>
      <span>→</span>
      <span className={accepted > 0 ? 'text-green-600 font-medium' : ''}>{accepted} joined</span>
      {invited > 0 && (
        <span className="text-xs ml-0.5">({rate}%)</span>
      )}
    </div>
  );
};

const SkeletonRow: React.FC = () => (
  <tr className="border-b border-border animate-pulse">
    {Array.from({ length: 9 }).map((_, i) => (
      <td key={i} className="p-4">
        <div className="h-4 bg-muted rounded w-20" />
      </td>
    ))}
  </tr>
);

// ─── Main Component ──────────────────────────────────────────────────────────
const SandboxTenants: React.FC = () => {
  const [tenants, setTenants]           = useState<SandboxTenantRow[]>([]);
  const [loading, setLoading]           = useState(true);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab]       = useState<FilterTab>('all');
  const [extendingTenant, setExtendingTenant]   = useState<string | null>(null);
  const [resendingInvestor, setResendingInvestor] = useState<string | null>(null);
  const [loggingInAs, setLoggingInAs]   = useState<string | null>(null);
  const [removingMember, setRemovingMember]     = useState<string | null>(null);
  const [addingInvestor, setAddingInvestor]     = useState<string | null>(null);
  const [addingAdmin, setAddingAdmin]           = useState<string | null>(null);
  const [newInvestorEmail, setNewInvestorEmail] = useState<Record<string, string>>({});
  const [newAdminEmail, setNewAdminEmail]       = useState<Record<string, string>>({});
  const { loginAs } = useUser();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res: any = await apiRequest('GET', '/sandbox/onboarding/tenants');
      setTenants(res?.tenants || res?.data || []);
    } catch (err) {
      toast.error('Failed to load sandbox tenants');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleRow = (id: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const extendTrial = async (tenantId: string) => {
    setExtendingTenant(tenantId);
    try {
      await apiRequest('POST', '/sandbox/extend', { tenantId, days: 7 });
      toast.success('Trial extended by 7 days');
      await load();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to extend trial');
    } finally {
      setExtendingTenant(null);
    }
  };

  const resendInvite = async (investorId: string, email: string) => {
    setResendingInvestor(investorId);
    try {
      await apiRequest('POST', `/sandbox/onboarding/investors/${investorId}/resend`);
      toast.success(`Invite resent to ${email}`);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to resend invite');
    } finally {
      setResendingInvestor(null);
    }
  };

  const loginAsUser = async (userId: string, email: string, tenantId: string, role: 'investor' | 'admin') => {
    setLoggingInAs(userId);
    try {
      const ok = await loginAs(userId, email);
      if (ok) {
        // Impersonated user lands in their tenant portal, not REDEX admin
        window.location.href = role === 'admin'
          ? `/${tenantId}/portal`
          : `/${tenantId}/portal`;
      }
    } finally {
      setLoggingInAs(null);
    }
  };

  const copyUrl = (url: string, label: string) => {
    navigator.clipboard.writeText(url).then(() => {
      toast.success(`${label} copied to clipboard`);
    }).catch(() => {
      toast.error('Clipboard access denied');
    });
  };

  const addInvestor = async (tenantId: string) => {
    const email = (newInvestorEmail[tenantId] || '').trim().toLowerCase();
    if (!email) return;
    setAddingInvestor(tenantId);
    try {
      const res: any = await apiRequest('POST', '/sandbox/onboarding/investors', { tenantId, email });
      if (res?.success === false) throw new Error(res.error || 'Failed');
      toast.success(`${email} added as investor`);
      setNewInvestorEmail(prev => ({ ...prev, [tenantId]: '' }));
      await load();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to add investor');
    } finally {
      setAddingInvestor(null);
    }
  };

  const removeInvestor = async (investorId: string, tenantId: string, email: string) => {
    setRemovingMember(investorId);
    try {
      const res: any = await apiRequest('DELETE', `/sandbox/onboarding/investors/${investorId}?tenantId=${encodeURIComponent(tenantId)}`);
      if (res?.success === false) throw new Error(res.error || 'Failed');
      toast.success(`${email} removed`);
      await load();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to remove investor');
    } finally {
      setRemovingMember(null);
    }
  };

  const addAdmin = async (tenantId: string) => {
    const email = (newAdminEmail[tenantId] || '').trim().toLowerCase();
    if (!email) return;
    setAddingAdmin(tenantId);
    try {
      const res: any = await apiRequest('POST', '/sandbox/onboarding/admins', { tenantId, email });
      if (res?.success === false) throw new Error(res.error || 'Failed');
      toast.success(`${email} added as admin`);
      setNewAdminEmail(prev => ({ ...prev, [tenantId]: '' }));
      await load();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to add admin');
    } finally {
      setAddingAdmin(null);
    }
  };

  const removeAdmin = async (adminId: string, tenantId: string, email: string) => {
    setRemovingMember(adminId);
    try {
      const res: any = await apiRequest('DELETE', `/sandbox/onboarding/admins/${adminId}?tenantId=${encodeURIComponent(tenantId)}`);
      if (res?.success === false) throw new Error(res.error || 'Failed');
      toast.success(`${email} removed`);
      await load();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to remove admin');
    } finally {
      setRemovingMember(null);
    }
  };

  // ── Filtering ──────────────────────────────────────────────────────────────
  const filtered = tenants.filter(t => {
    if (activeTab === 'all')        return true;
    if (activeTab === 'active')     return trialStatus(t) === 'active';
    if (activeTab === 'expiring')   return trialStatus(t) === 'expiring';
    if (activeTab === 'expired')    return trialStatus(t) === 'expired';
    if (activeTab === 'incomplete') return !t.wizard_completed;
    return true;
  });

  // ── Aggregate stats ────────────────────────────────────────────────────────
  const stats = {
    total:     tenants.length,
    active:    tenants.filter(t => trialStatus(t) === 'active').length,
    expiring:  tenants.filter(t => trialStatus(t) === 'expiring').length,
    expired:   tenants.filter(t => trialStatus(t) === 'expired').length,
    incomplete: tenants.filter(t => !t.wizard_completed).length,
    totalInvestors: tenants.reduce((s, t) => s + (t.investors?.length || 0), 0),
    joinedInvestors: tenants.reduce((s, t) => s + (t.investors?.filter(i => i.accepted_at).length || 0), 0),
  };

  const tabs: { key: FilterTab; label: string; count: number }[] = [
    { key: 'all',        label: 'All',          count: stats.total },
    { key: 'active',     label: 'Active',       count: stats.active },
    { key: 'expiring',   label: 'Expiring',     count: stats.expiring },
    { key: 'expired',    label: 'Expired',      count: stats.expired },
    { key: 'incomplete', label: 'Incomplete',   count: stats.incomplete },
  ];

  return (
    <div className="space-y-6">

      {/* ── Command Center Header ── */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Zap className="h-5 w-5 text-amber-500" />
            <h1 className="text-2xl font-display font-semibold text-[#242424]">
              Sandbox Command Center
            </h1>
          </div>
          <p className="text-sm text-muted-foreground">
            Operate and monitor all active sandbox tenants from one pane of glass
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/admin/sandbox/setup')}
          >
            <Plus className="h-4 w-4 mr-1.5" />
            New Sandbox
          </Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* ── Platform KPI strip ── */}
      {!loading && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <Card className="border-border col-span-1">
            <CardContent className="p-4">
              <p className="text-2xl font-bold text-[#242424]">{stats.total}</p>
              <p className="text-xs text-muted-foreground">Total</p>
            </CardContent>
          </Card>
          <Card className="border-border col-span-1">
            <CardContent className="p-4">
              <p className="text-2xl font-bold text-green-600">{stats.active}</p>
              <p className="text-xs text-muted-foreground">Active</p>
            </CardContent>
          </Card>
          <Card className="border-border col-span-1">
            <CardContent className="p-4">
              <p className="text-2xl font-bold text-amber-600">{stats.expiring}</p>
              <p className="text-xs text-muted-foreground">Expiring</p>
            </CardContent>
          </Card>
          <Card className="border-border col-span-1">
            <CardContent className="p-4">
              <p className="text-2xl font-bold text-red-600">{stats.expired}</p>
              <p className="text-xs text-muted-foreground">Expired</p>
            </CardContent>
          </Card>
          <Card className="border-border col-span-1">
            <CardContent className="p-4">
              <p className="text-2xl font-bold text-[#242424]">{stats.totalInvestors}</p>
              <p className="text-xs text-muted-foreground">Invited</p>
            </CardContent>
          </Card>
          <Card className="border-border col-span-1">
            <CardContent className="p-4">
              <p className="text-2xl font-bold text-blue-600">{stats.joinedInvestors}</p>
              <p className="text-xs text-muted-foreground">Joined</p>
            </CardContent>
          </Card>
          <Card className="border-border col-span-1">
            <CardContent className="p-4">
              <p className="text-2xl font-bold text-[#242424]">
                {stats.totalInvestors > 0
                  ? `${Math.round((stats.joinedInvestors / stats.totalInvestors) * 100)}%`
                  : '—'}
              </p>
              <p className="text-xs text-muted-foreground">Conversion</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── Filter Tabs ── */}
      <div className="flex items-center gap-1 border-b border-border">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-foreground text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
            {tab.count > 0 && (
              <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
                activeTab === tab.key
                  ? 'bg-foreground text-background'
                  : 'bg-muted text-muted-foreground'
              }`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Tenants Table ── */}
      <Card className="border-border">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full min-w-[1100px]">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="text-left p-4 text-xs text-muted-foreground w-8" />
                <th className="text-left p-4 text-xs text-muted-foreground">Tenant</th>
                <th className="text-left p-4 text-xs text-muted-foreground">Status</th>
                <th className="text-left p-4 text-xs text-muted-foreground">Funnel</th>
                <th className="text-left p-4 text-xs text-muted-foreground">Usage</th>
                <th className="text-left p-4 text-xs text-muted-foreground">Launched</th>
                <th className="text-left p-4 text-xs text-muted-foreground">Portal URL</th>
                <th className="text-left p-4 text-xs text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <>
                  <SkeletonRow />
                  <SkeletonRow />
                  <SkeletonRow />
                </>
              )}

              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="p-16 text-center">
                    <div className="flex flex-col items-center gap-3 text-muted-foreground">
                      <FlaskConical className="h-10 w-10 opacity-30" />
                      <p className="text-sm font-medium">
                        {activeTab === 'all' ? 'No sandbox tenants yet' : `No ${activeTab} tenants`}
                      </p>
                      {activeTab === 'all' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate('/admin/sandbox/setup')}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Create first sandbox
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              )}

              {!loading && filtered.map(tenant => {
                const isExpanded = expandedRows.has(tenant.tenant_id);
                const status     = trialStatus(tenant);
                const activity   = totalActivity(tenant);

                return (
                  <React.Fragment key={tenant.tenant_id}>
                    <tr
                      className="border-b border-border hover:bg-muted/30 cursor-pointer transition-colors"
                      onClick={() => toggleRow(tenant.tenant_id)}
                    >
                      {/* Expand */}
                      <td className="p-4 text-muted-foreground">
                        {isExpanded
                          ? <ChevronDown className="h-4 w-4" />
                          : <ChevronRight className="h-4 w-4" />
                        }
                      </td>

                      {/* Tenant identity */}
                      <td className="p-4">
                        <div className="flex items-center gap-2.5">
                          <div
                            className="h-8 w-8 rounded-lg flex-shrink-0 border border-border flex items-center justify-center text-white text-xs font-bold"
                            style={{ backgroundColor: tenant.primary_color || '#64748b' }}
                          >
                            {(tenant.company_name || tenant.tenant_id).charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-[#242424] flex items-center gap-1.5">
                              {tenant.company_name || tenant.tenant_id}
                              {tenant.wizard_completed && (
                                <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                              )}
                            </div>
                            <code className="text-[10px] text-muted-foreground font-mono">
                              {tenant.tenant_id}
                            </code>
                          </div>
                        </div>
                      </td>

                      {/* Trial status */}
                      <td className="p-4">
                        <div className="flex flex-col gap-1">
                          <TrialBadge tenant={tenant} />
                          {tenant.trial_expires_at && (
                            <span className="text-[10px] text-muted-foreground">
                              Expires {fmtDate(tenant.trial_expires_at)}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Conversion funnel */}
                      <td className="p-4">
                        <FunnelBar tenant={tenant} />
                        <div className="flex items-center gap-2 mt-1 text-[10px] text-muted-foreground">
                          <Users className="h-3 w-3" />
                          <span>{tenant.admin_count}/3 admins</span>
                        </div>
                      </td>

                      {/* Usage meters */}
                      <td className="p-4">
                        <div className="space-y-1.5 min-w-[120px]">
                          <UsageBar used={tenant.esigns_used}      limit={tenant.esigns_limit}      label="E-Signs" />
                          <UsageBar used={tenant.offerings_count}  limit={tenant.offerings_limit}   label="Offerings" />
                          <UsageBar used={tenant.properties_count} limit={tenant.properties_limit}  label="Properties" />
                        </div>
                      </td>

                      {/* Launch date */}
                      <td className="p-4">
                        {tenant.launched_at ? (
                          <div>
                            <div className="text-xs text-[#242424]">{fmtDate(tenant.launched_at)}</div>
                            {activity > 0 && (
                              <div className="text-[10px] text-green-600 flex items-center gap-0.5 mt-0.5">
                                <TrendingUp className="h-3 w-3" />
                                {activity} actions
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-xs text-amber-600 flex items-center gap-1">
                            <AlertTriangle className="h-3.5 w-3.5" />
                            Not launched
                          </div>
                        )}
                      </td>

                      {/* Portal URL */}
                      <td className="p-4" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          <a
                            href={loginUrl(tenant.tenant_id)}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[10px] text-blue-600 hover:underline flex items-center gap-1 truncate max-w-[160px]"
                            title={loginUrl(tenant.tenant_id)}
                          >
                            <ExternalLink className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">/{tenant.tenant_id}/login</span>
                          </a>
                          <button
                            onClick={() => copyUrl(loginUrl(tenant.tenant_id), 'Login URL')}
                            className="text-muted-foreground hover:text-foreground p-1 rounded"
                            title="Copy login URL"
                          >
                            <Copy className="h-3 w-3" />
                          </button>
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="p-4" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-1.5">
                          {/* Open portal */}
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-xs"
                            onClick={() => window.open(loginUrl(tenant.tenant_id), '_blank')}
                            title="Open tenant portal"
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            Portal
                          </Button>

                          {/* Extend trial */}
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-xs"
                            disabled={extendingTenant === tenant.tenant_id}
                            onClick={() => extendTrial(tenant.tenant_id)}
                            title="Extend trial by 7 days"
                          >
                            {extendingTenant === tenant.tenant_id
                              ? <RefreshCw className="h-3 w-3 animate-spin" />
                              : <Clock className="h-3 w-3" />
                            }
                          </Button>
                        </div>
                      </td>
                    </tr>

                    {/* ── Expanded detail ── */}
                    {isExpanded && (
                      <tr className="border-b border-border bg-muted/20">
                        <td colSpan={8} className="px-8 py-5">
                          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                            {/* Investors */}
                            <div>
                              <h4 className="text-xs font-semibold text-[#242424] uppercase tracking-wider mb-3 flex items-center gap-1.5">
                                <Users className="h-3.5 w-3.5 text-muted-foreground" />
                                Investors ({tenant.investors?.length || 0} / 5)
                              </h4>
                              {tenant.investors?.length > 0 ? (
                                <div className="space-y-2">
                                  {tenant.investors.map(inv => (
                                    <div
                                      key={inv.id}
                                      className="flex items-center justify-between bg-background border border-border rounded-lg px-3 py-2"
                                    >
                                      <div className="min-w-0 flex-1">
                                        <div className="text-xs font-medium text-[#242424] truncate">{inv.email}</div>
                                        {inv.name && (
                                          <div className="text-[10px] text-muted-foreground">{inv.name}</div>
                                        )}
                                        <div className="text-[10px] mt-0.5">
                                          {inv.accepted_at ? (
                                            <span className="text-green-600">Joined {fmtDate(inv.accepted_at)}</span>
                                          ) : inv.invite_sent_at ? (
                                            <span className="text-amber-600">Invited {fmtDate(inv.invite_sent_at)}</span>
                                          ) : (
                                            <span className="text-muted-foreground">Not invited</span>
                                          )}
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-1.5 ml-2 flex-shrink-0">
                                        {inv.accepted_at
                                          ? <Badge className="text-[10px] bg-green-50 text-green-700 border-green-200">Joined</Badge>
                                          : <Badge className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">Pending</Badge>
                                        }
                                        <button
                                          className="p-1 text-muted-foreground hover:text-foreground rounded"
                                          disabled={resendingInvestor === inv.id}
                                          onClick={() => resendInvite(inv.id, inv.email)}
                                          title="Resend invite"
                                        >
                                          {resendingInvestor === inv.id
                                            ? <RefreshCw className="h-3 w-3 animate-spin" />
                                            : <Mail className="h-3 w-3" />
                                          }
                                        </button>
                                        {inv.user_id && (
                                          <button
                                            className="p-1 text-blue-600 hover:text-blue-700 rounded"
                                            disabled={loggingInAs === inv.user_id}
                                            onClick={() => loginAsUser(inv.user_id!, inv.email, tenant.tenant_id, 'investor')}
                                            title="Impersonate investor"
                                          >
                                            {loggingInAs === inv.user_id
                                              ? <RefreshCw className="h-3 w-3 animate-spin" />
                                              : <LogIn className="h-3 w-3" />
                                            }
                                          </button>
                                        )}
                                        <button
                                          className="p-1 text-red-400 hover:text-red-600 rounded"
                                          disabled={removingMember === inv.id}
                                          onClick={() => removeInvestor(inv.id, tenant.tenant_id, inv.email)}
                                          title="Remove investor"
                                        >
                                          {removingMember === inv.id
                                            ? <RefreshCw className="h-3 w-3 animate-spin" />
                                            : <Trash2 className="h-3 w-3" />
                                          }
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-xs text-muted-foreground">No investors invited yet</p>
                              )}
                              {(tenant.investors?.length || 0) < 5 && (
                                <div className="flex gap-1.5 mt-2">
                                  <input
                                    type="email"
                                    placeholder="investor@email.com"
                                    value={newInvestorEmail[tenant.tenant_id] || ''}
                                    onChange={e => setNewInvestorEmail(prev => ({ ...prev, [tenant.tenant_id]: e.target.value }))}
                                    onKeyDown={e => e.key === 'Enter' && addInvestor(tenant.tenant_id)}
                                    className="flex-1 text-xs px-2 py-1.5 border border-border rounded-md bg-background focus:outline-none focus:ring-1 focus:ring-ring"
                                  />
                                  <button
                                    className="flex items-center gap-1 text-xs px-2.5 py-1.5 bg-foreground text-background rounded-md hover:opacity-90 disabled:opacity-50"
                                    disabled={addingInvestor === tenant.tenant_id || !newInvestorEmail[tenant.tenant_id]?.trim()}
                                    onClick={() => addInvestor(tenant.tenant_id)}
                                    title="Add investor"
                                  >
                                    {addingInvestor === tenant.tenant_id
                                      ? <RefreshCw className="h-3 w-3 animate-spin" />
                                      : <UserPlus className="h-3 w-3" />
                                    }
                                    Add
                                  </button>
                                </div>
                              )}
                            </div>

                            {/* Admins */}
                            <div>
                              <h4 className="text-xs font-semibold text-[#242424] uppercase tracking-wider mb-3 flex items-center gap-1.5">
                                <Users className="h-3.5 w-3.5 text-muted-foreground" />
                                Sandbox Admins ({tenant.admins?.length || 0} / 3)
                              </h4>
                              {tenant.admins?.length > 0 ? (
                                <div className="space-y-2">
                                  {tenant.admins.map(admin => (
                                    <div
                                      key={admin.id}
                                      className="flex items-center justify-between bg-background border border-border rounded-lg px-3 py-2"
                                    >
                                      <div className="min-w-0 flex-1">
                                        <div className="text-xs font-medium text-[#242424] truncate">{admin.email}</div>
                                        <div className="text-[10px] mt-0.5">
                                          {admin.accepted_at
                                            ? <span className="text-green-600">Active since {fmtDate(admin.accepted_at)}</span>
                                            : <span className="text-amber-600">Invite pending</span>
                                          }
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-1.5 ml-2 flex-shrink-0">
                                        {admin.accepted_at
                                          ? <Badge className="text-[10px] bg-green-50 text-green-700 border-green-200">Active</Badge>
                                          : <Badge className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">Pending</Badge>
                                        }
                                        {admin.user_id && admin.accepted_at && (
                                          <button
                                            className="p-1 text-blue-600 hover:text-blue-700 rounded"
                                            disabled={loggingInAs === admin.user_id}
                                            onClick={() => loginAsUser(admin.user_id!, admin.email, tenant.tenant_id, 'admin')}
                                            title="Impersonate admin"
                                          >
                                            {loggingInAs === admin.user_id
                                              ? <RefreshCw className="h-3 w-3 animate-spin" />
                                              : <LogIn className="h-3 w-3" />
                                            }
                                          </button>
                                        )}
                                        <button
                                          className="p-1 text-red-400 hover:text-red-600 rounded"
                                          disabled={removingMember === admin.id}
                                          onClick={() => removeAdmin(admin.id, tenant.tenant_id, admin.email)}
                                          title="Remove admin"
                                        >
                                          {removingMember === admin.id
                                            ? <RefreshCw className="h-3 w-3 animate-spin" />
                                            : <Trash2 className="h-3 w-3" />
                                          }
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-xs text-muted-foreground">No admins added yet</p>
                              )}
                              {(tenant.admins?.length || 0) < 3 && (
                                <div className="flex gap-1.5 mt-2">
                                  <input
                                    type="email"
                                    placeholder="admin@email.com"
                                    value={newAdminEmail[tenant.tenant_id] || ''}
                                    onChange={e => setNewAdminEmail(prev => ({ ...prev, [tenant.tenant_id]: e.target.value }))}
                                    onKeyDown={e => e.key === 'Enter' && addAdmin(tenant.tenant_id)}
                                    className="flex-1 text-xs px-2 py-1.5 border border-border rounded-md bg-background focus:outline-none focus:ring-1 focus:ring-ring"
                                  />
                                  <button
                                    className="flex items-center gap-1 text-xs px-2.5 py-1.5 bg-foreground text-background rounded-md hover:opacity-90 disabled:opacity-50"
                                    disabled={addingAdmin === tenant.tenant_id || !newAdminEmail[tenant.tenant_id]?.trim()}
                                    onClick={() => addAdmin(tenant.tenant_id)}
                                    title="Add admin"
                                  >
                                    {addingAdmin === tenant.tenant_id
                                      ? <RefreshCw className="h-3 w-3 animate-spin" />
                                      : <UserPlus className="h-3 w-3" />
                                    }
                                    Add
                                  </button>
                                </div>
                              )}
                            </div>

                            {/* Portal links & controls */}
                            <div>
                              <h4 className="text-xs font-semibold text-[#242424] uppercase tracking-wider mb-3">
                                Portal Access
                              </h4>
                              <div className="space-y-3">
                                {/* Branding preview */}
                                <div
                                  className="h-16 rounded-lg border border-border flex items-center justify-center relative overflow-hidden"
                                  style={{ backgroundColor: tenant.primary_color || '#0f172a' }}
                                >
                                  <div
                                    className="absolute bottom-0 left-0 right-0 h-1"
                                    style={{ backgroundColor: tenant.accent_color || '#3b82f6' }}
                                  />
                                  <span className="text-white text-sm font-semibold drop-shadow">
                                    {tenant.company_name || tenant.tenant_id}
                                  </span>
                                </div>

                                {/* URL cards */}
                                <div className="space-y-2">
                                  <div className="border border-border rounded-lg p-2.5 bg-background">
                                    <div className="text-[10px] text-muted-foreground mb-1 font-medium uppercase tracking-wider">Investor Login</div>
                                    <div className="flex items-center gap-1.5">
                                      <span className="text-xs text-[#242424] font-mono truncate flex-1">
                                        {loginUrl(tenant.tenant_id)}
                                      </span>
                                      <button
                                        onClick={() => copyUrl(loginUrl(tenant.tenant_id), 'Login URL')}
                                        className="p-1 text-muted-foreground hover:text-foreground rounded flex-shrink-0"
                                      >
                                        <Copy className="h-3 w-3" />
                                      </button>
                                      <a
                                        href={loginUrl(tenant.tenant_id)}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="p-1 text-blue-600 hover:text-blue-700 rounded flex-shrink-0"
                                      >
                                        <ExternalLink className="h-3 w-3" />
                                      </a>
                                    </div>
                                  </div>

                                  <div className="border border-border rounded-lg p-2.5 bg-background">
                                    <div className="text-[10px] text-muted-foreground mb-1 font-medium uppercase tracking-wider">Investor Portal</div>
                                    <div className="flex items-center gap-1.5">
                                      <span className="text-xs text-[#242424] font-mono truncate flex-1">
                                        {portalUrl(tenant.tenant_id)}
                                      </span>
                                      <button
                                        onClick={() => copyUrl(portalUrl(tenant.tenant_id), 'Portal URL')}
                                        className="p-1 text-muted-foreground hover:text-foreground rounded flex-shrink-0"
                                      >
                                        <Copy className="h-3 w-3" />
                                      </button>
                                    </div>
                                  </div>
                                </div>

                                {/* Trial controls */}
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="flex-1 text-xs"
                                    disabled={extendingTenant === tenant.tenant_id}
                                    onClick={() => extendTrial(tenant.tenant_id)}
                                  >
                                    {extendingTenant === tenant.tenant_id
                                      ? <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                                      : <Clock className="h-3 w-3 mr-1" />
                                    }
                                    Extend +7d
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="default"
                                    className="flex-1 text-xs"
                                    onClick={() => window.open(loginUrl(tenant.tenant_id), '_blank')}
                                  >
                                    <Eye className="h-3 w-3 mr-1" />
                                    View Portal
                                  </Button>
                                </div>
                              </div>
                            </div>

                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

export default SandboxTenants;
